import React, { useState, useEffect, useMemo } from 'react';
import { Card, Row, Col, Button, Modal, Form, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import FalconCardHeader from '../common/FalconCardHeader';
import AdvanceTable from '../common/advance-table/AdvanceTable';
import AdvanceTablePagination from '../common/advance-table/AdvanceTablePagination';
import AdvanceTableProvider from '../../providers/AdvanceTableProvider';
import IconButton from '../common/IconButton';
import useAdvanceTable from '../../hooks/useAdvanceTable';
import { toast } from 'react-toastify';
import { tenantService } from '../../services';
import PageHeader from '../common/PageHeader';

const TenantManagement = () => {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    subdomain: '',
    contactEmail: '',
    contactPhone: '',
    plan: 'basic',
    isActive: true
  });

  const plans = [
    { value: 'basic', label: 'Básico', color: 'secondary' },
    { value: 'premium', label: 'Premium', color: 'info' },
    { value: 'enterprise', label: 'Enterprise', color: 'success' }
  ];

  // Cargar datos reales desde la API
  useEffect(() => {
    loadTenants();
  }, []);

  const loadTenants = async () => {
    setLoading(true);
    try {
      const response = await tenantService.getAllTenants();
      if (response.success) {
        setTenants(response.tenants || response.data || []);
      } else {
        toast.error('Error al cargar tenants: ' + response.error);
        setTenants([]);
      }
    } catch (error) {
      console.error('Error cargando tenants:', error);
      toast.error('Error de conexión al cargar tenants');
      setTenants([]);
    } finally {
      setLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: 'Empresa',
        meta: {
          headerProps: { className: 'text-800' }
        },
        cell: ({ row: { original } }) => (
          <div className="d-flex align-items-center">
            <div>
              <div className="fw-semibold">
                {original.name}
                {original.isAdmin && (
                  <FontAwesomeIcon 
                    icon="crown" 
                    className="ms-2 text-warning" 
                    title="Empresa Administradora"
                  />
                )}
              </div>
              <small className="text-600">{original.code}</small>
            </div>
          </div>
        )
      },
      {
        accessorKey: 'subdomain',
        header: 'Subdominio',
        meta: {
          headerProps: { className: 'text-800' }
        },
        cell: ({ getValue, row }) => (
          <code className="text-primary">
            {getValue() || row.original.code.toLowerCase()}.galaxymetrics.com
          </code>
        )
      },
      {
        accessorKey: 'plan',
        header: 'Plan',
        meta: {
          headerProps: { className: 'text-800' }
        },
        cell: ({ getValue }) => {
          const plan = plans.find(p => p.value === getValue());
          return <Badge bg={plan?.color || 'secondary'}>{plan?.label || getValue()}</Badge>;
        }
      },
      {
        accessorKey: 'users',
        header: 'Usuarios',
        meta: {
          headerProps: { className: 'text-center text-800' },
          cellProps: { className: 'text-center' }
        },
        cell: ({ getValue }) => (
          <Badge bg="info" pill>
            {getValue()?.length || 0}
          </Badge>
        )
      },
      {
        accessorKey: 'isActive',
        header: 'Estado',
        meta: {
          headerProps: { className: 'text-center text-800' },
          cellProps: { className: 'text-center' }
        },
        cell: ({ getValue }) => (
          <Badge bg={getValue() ? 'success' : 'danger'}>
            {getValue() ? 'Activo' : 'Inactivo'}
          </Badge>
        )
      },
      {
        accessorKey: 'createdAt',
        header: 'Creada',
        meta: {
          headerProps: { className: 'text-800' }
        },
        cell: ({ getValue }) => {
          const date = new Date(getValue());
          return <span className="text-600">{date.toLocaleDateString('es-ES')}</span>;
        }
      },
      {
        accessorKey: 'actions',
        header: 'Acciones',
        meta: {
          headerProps: { className: 'text-end text-800' },
          cellProps: { className: 'text-end' }
        },
        enableSorting: false,
        cell: ({ row }) => (
          <div className="d-flex gap-1 justify-content-end">
            <IconButton
              variant="falcon-default"
              size="sm"
              icon="edit"
              onClick={() => handleEdit(row.original)}
              title="Editar"
            />
            <IconButton
              variant={row.original.isActive ? 'falcon-default' : 'outline-success'}
              size="sm"
              icon={row.original.isActive ? 'toggle-off' : 'toggle-on'}
              onClick={() => handleToggleStatus(row.original.id)}
              title={row.original.isActive ? 'Desactivar' : 'Activar'}
              disabled={row.original.isAdmin}
            />
          </div>
        )
      }
    ],
    [tenants, plans, handleToggleStatus]
  );

  const table = useAdvanceTable({
    data: tenants,
    columns,
    sortable: true,
    pagination: true,
    perPage: 10
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      let response;
      if (editingTenant) {
        response = await tenantService.updateTenant(editingTenant.id, formData);
        if (response.success) {
          toast.success('Tenant actualizado exitosamente');
          loadTenants();
        } else {
          toast.error('Error al actualizar tenant: ' + response.error);
        }
      } else {
        response = await tenantService.createTenant(formData);
        if (response.success) {
          toast.success('Tenant creado exitosamente');
          loadTenants();
        } else {
          toast.error('Error al crear tenant: ' + response.error);
        }
      }
      
      if (response.success) {
        setShowModal(false);
        resetForm();
      }
    } catch (error) {
      console.error('Error en operación:', error);
      toast.error('Error de conexión');
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      code: '',
      subdomain: '',
      contactEmail: '',
      contactPhone: '',
      plan: 'basic',
      isActive: true
    });
    setEditingTenant(null);
  };

  const handleEdit = (tenant) => {
    setEditingTenant(tenant);
    setFormData({
      name: tenant.name,
      code: tenant.code,
      subdomain: tenant.subdomain || '',
      contactEmail: tenant.contactEmail || '',
      contactPhone: tenant.contactPhone || '',
      plan: tenant.plan,
      isActive: tenant.isActive
    });
    setShowModal(true);
  };

  const handleToggleStatus = async (tenantId) => {
    try {
      const tenant = tenants.find(t => t.id === tenantId);
      const response = await tenantService.updateTenantStatus(tenantId, !tenant.isActive);
      
      if (response.success) {
        toast.success(`Tenant ${!tenant.isActive ? 'activado' : 'desactivado'} exitosamente`);
        loadTenants();
      } else {
        toast.error('Error al cambiar estado: ' + response.error);
      }
    } catch (error) {
      console.error('Error cambiando estado:', error);
      toast.error('Error de conexión');
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Cargando...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title="Gestión de Tenants"
        description="Administra las empresas y organizaciones del sistema"
        className="mb-3"
      >
        <Button
          variant="primary"
          size="sm"
          onClick={() => setShowModal(true)}
        >
          <FontAwesomeIcon icon="plus" className="me-2" />
          Nueva Empresa
        </Button>
      </PageHeader>

      <AdvanceTableProvider {...table}>
        <Card>
          <FalconCardHeader 
            title="Empresas Registradas"
            titleTag="h6"
            className="py-2"
            endEl={
              <Badge bg="light" text="dark" className="fs-10">
                {tenants.length} empresas
              </Badge>
            }
          />
          <Card.Body className="p-0">
            <AdvanceTable
              headerClassName="bg-200 text-nowrap align-middle"
              rowClassName="align-middle white-space-nowrap"
              tableProps={{
                className: 'fs-10 mb-0 overflow-hidden',
                size: 'sm'
              }}
            />
          </Card.Body>
          <Card.Footer>
            <AdvanceTablePagination />
          </Card.Footer>
        </Card>
      </AdvanceTableProvider>

      {/* Modal para crear/editar tenant */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={editingTenant ? "edit" : "plus"} className="me-2 text-primary" />
            {editingTenant ? 'Editar Empresa' : 'Nueva Empresa'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fs-10 fw-bold text-600">
                    <FontAwesomeIcon icon="building" className="me-1 text-primary" />
                    Nombre de la Empresa *
                  </Form.Label>
                  <Form.Control
                    size="sm"
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    required
                    placeholder="Ej: Empresa ABC"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fs-10 fw-bold text-600">
                    <FontAwesomeIcon icon="code" className="me-1 text-primary" />
                    Código *
                  </Form.Label>
                  <Form.Control
                    size="sm"
                    type="text"
                    value={formData.code}
                    onChange={(e) => handleInputChange('code', e.target.value.toUpperCase())}
                    required
                    placeholder="Ej: EMPRESA_ABC"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fs-10 fw-bold text-600">
                    <FontAwesomeIcon icon="globe" className="me-1 text-success" />
                    Subdominio
                  </Form.Label>
                  <Form.Control
                    size="sm"
                    type="text"
                    value={formData.subdomain}
                    onChange={(e) => handleInputChange('subdomain', e.target.value.toLowerCase())}
                    placeholder="Ej: empresa-abc"
                  />
                  <Form.Text className="text-muted fs-11">
                    Se usará el código si está vacío
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fs-10 fw-bold text-600">
                    <FontAwesomeIcon icon="crown" className="me-1 text-warning" />
                    Plan
                  </Form.Label>
                  <Form.Select
                    size="sm"
                    value={formData.plan}
                    onChange={(e) => handleInputChange('plan', e.target.value)}
                  >
                    {plans.map(plan => (
                      <option key={plan.value} value={plan.value}>
                        {plan.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fs-10 fw-bold text-600">
                    <FontAwesomeIcon icon="envelope" className="me-1 text-info" />
                    Email de Contacto
                  </Form.Label>
                  <Form.Control
                    size="sm"
                    type="email"
                    value={formData.contactEmail}
                    onChange={(e) => handleInputChange('contactEmail', e.target.value)}
                    placeholder="contacto@empresa.com"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fs-10 fw-bold text-600">
                    <FontAwesomeIcon icon="phone" className="me-1 text-info" />
                    Teléfono de Contacto
                  </Form.Label>
                  <Form.Control
                    size="sm"
                    type="tel"
                    value={formData.contactPhone}
                    onChange={(e) => handleInputChange('contactPhone', e.target.value)}
                    placeholder="+593 99 123 4567"
                  />
                </Form.Group>
              </Col>
              <Col xs={12}>
                <Form.Check
                  type="checkbox"
                  id="isActive"
                  label="Empresa activa"
                  checked={formData.isActive}
                  onChange={(e) => handleInputChange('isActive', e.target.checked)}
                  className="fs-10"
                />
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button 
              variant="outline-secondary" 
              size="sm"
              onClick={() => setShowModal(false)}
              disabled={saving}
            >
              <FontAwesomeIcon icon="times" className="me-1" />
              Cancelar
            </Button>
            <Button 
              type="submit" 
              variant="primary"
              size="sm"
              disabled={saving}
            >
              {saving ? (
                <>
                  <div className="spinner-border spinner-border-sm me-2" role="status">
                    <span className="visually-hidden">Guardando...</span>
                  </div>
                  Guardando...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={editingTenant ? "check" : "plus"} className="me-1" />
                  {editingTenant ? 'Actualizar' : 'Crear'}
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export default TenantManagement; 